import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { MagnifyingGlass, SpinnerGap, UserPlus, UsersThree } from '@phosphor-icons/react';

const SOURCE_LABELS = {
  website: 'Website',
  web_search: 'Web Search',
  linkedin_snippet: 'LinkedIn',
};

const HR_TITLE_PATTERN = /(?:\bhr\b|human resources|people(?:\s*&\s*culture| partner)?|talent|recruit|personalleitung|personalreferent)/i;

function isHrCandidate(candidate) {
  return HR_TITLE_PATTERN.test(candidate?.title || '');
}

const GENERIC_LOCAL_PARTS = new Set([
  'info', 'sales', 'contact', 'office', 'hello', 'support', 'admin', 'help',
  'team', 'service', 'billing', 'accounts', 'general', 'enquiries', 'inquiries',
  'reception', 'mail', 'marketing', 'webmaster',
]);

function contactEmailKey(email) {
  return (email || '').trim().toLowerCase();
}

function emailLocalPart(email) {
  const key = contactEmailKey(email);
  if (!key.includes('@')) return '';
  return key.split('@')[0];
}

export function isGenericCompanyEmail(email) {
  return GENERIC_LOCAL_PARTS.has(emailLocalPart(email));
}

export function isGenericContact(contact) {
  const source = contact?.contact_source;
  const emailType = contact?.email_type;
  return (
    source === 'auto_generated'
    || emailType === 'generic_business'
    || emailType === 'role_based'
    || isGenericCompanyEmail(contact?.email)
  );
}

export function ContactTypeBadge({ contact, candidateEmail, candidateType }) {
  const type = candidateType || contact?.candidate_type;

  if (type === 'generic_company_email' || (contact ? isGenericContact(contact) : isGenericCompanyEmail(candidateEmail))) {
    return (
      <span
        className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-gray-100 text-gray-600 border border-gray-200"
        data-testid="badge-generic"
      >
        Generic
      </span>
    );
  }

  if (type === 'generated_guess') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-amber-50 text-amber-800 border border-amber-200">
        Generated Guess
      </span>
    );
  }

  if (type === 'company_content') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-gray-50 text-gray-600 border border-gray-200">
        Company Content
      </span>
    );
  }

  if (
    type === 'personal_contact'
    || contact?.contact_source === 'personal_discovery'
    || contact?.email_type === 'named_business'
    || (candidateEmail && !isGenericCompanyEmail(candidateEmail))
  ) {
    return (
      <span
        className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-sky-50 text-sky-800 border border-sky-200"
        data-testid="badge-personal"
      >
        Personal
      </span>
    );
  }

  return (
    <span className="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
      {contact?.email_type || type || 'unknown'}
    </span>
  );
}

export function ConfidenceBadge({ score }) {
  const value = Number(score) || 0;
  let classes = 'bg-red-50 text-red-700 border-red-200';
  let label = 'Low';

  if (value >= 80) {
    classes = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    label = 'High';
  } else if (value >= 50) {
    classes = 'bg-amber-50 text-amber-800 border-amber-200';
    label = 'Medium';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono border ${classes}`}
      title={`${label} confidence`}
      data-testid="badge-confidence"
    >
      <span className="font-medium">{label}</span>
      <span className="opacity-70">{value}</span>
    </span>
  );
}

function ContactsTable({ rows, emptyMessage }) {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-md bg-white overflow-x-auto">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left w-10">Select</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Name</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Title</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Email</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Source</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Confidence</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Type</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

const DiscoveredContactsPanel = forwardRef(function DiscoveredContactsPanel({
  companyId,
  companyContacts = [],
  hasDomain = false,
  isSuperAdmin = false,
  showFindButton = true,
  onImported,
}, ref) {
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [discoveryRan, setDiscoveryRan] = useState(false);
  const [showContentMatches, setShowContentMatches] = useState(false);
  const [discoveryDiagnostics, setDiscoveryDiagnostics] = useState(null);
  const [importingIds, setImportingIds] = useState(new Set());
  const [importingAll, setImportingAll] = useState(false);
  const [importedEmails, setImportedEmails] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const visibleDiscovered = useMemo(
    () => discovered
      .filter((candidate) => showContentMatches || candidate.candidate_type !== 'company_content')
      .sort((left, right) => {
        const hrPriority = Number(isHrCandidate(right)) - Number(isHrCandidate(left));
        if (hrPriority !== 0) return hrPriority;
        return (Number(right.confidence) || 0) - (Number(left.confidence) || 0);
      }),
    [discovered, showContentMatches],
  );

  const contentMatchCount = useMemo(
    () => discovered.filter((candidate) => candidate.candidate_type === 'company_content').length,
    [discovered],
  );

  const genericCompanyContacts = useMemo(
    () => (companyContacts || []).filter(isGenericContact),
    [companyContacts],
  );

  useEffect(() => {
    const genericIds = genericCompanyContacts.map(
      (contact, index) => `generic:${contact.id || contact.email || index}`,
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of genericIds) next.add(id);
      return next;
    });
  }, [genericCompanyContacts]);

  useEffect(() => {
    let active = true;
    setDiscovered([]);
    setDiscoveryRan(false);
    setDiscoveryDiagnostics(null);

    const loadLatestDiscovery = async () => {
      try {
        const { data: jobsData } = await api.get(`/contact-discovery/companies/${companyId}/jobs`);
        const latest = (jobsData.items || []).find(
          (job) => job.status === 'completed' && Number(job.candidate_count) > 0,
        );
        if (!latest || !active) return;
        const { data: jobData } = await api.get(`/contact-discovery/jobs/${latest.id}`);
        if (!active) return;
        const candidates = jobData.candidates || [];
        setDiscovered(candidates);
        setDiscoveryRan(true);
        setSelectedIds(new Set());
      } catch (_error) {
        // A missing previous run should not block a new discovery.
      }
    };

    loadLatestDiscovery();
    return () => { active = false; };
  }, [companyId]);

  const existingEmails = useMemo(() => {
    const emails = new Set();
    for (const contact of companyContacts) {
      const key = contactEmailKey(contact.email);
      if (key) emails.add(key);
    }
    return emails;
  }, [companyContacts]);

  const toggleSelected = (rowId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const discoverContacts = async () => {
    if (!hasDomain) {
      toast.error('This company needs a domain before personal contact discovery can run.');
      return;
    }

    setDiscovering(true);
    try {
      const { data } = await api.post(`/contact-discovery/companies/${companyId}/discover`, {});
      const candidates = data.candidates || [];
      const importableCandidates = candidates.filter(
        (candidate) => candidate.candidate_type !== 'company_content',
      );

      console.log('[ContactDiscovery]', {
        company_id: companyId,
        job_id: data.job_id,
        contacts_discovered: importableCandidates.length,
        content_matches_hidden: candidates.length - importableCandidates.length,
        rejected_guesses: data.rejected_guesses,
        rejection_reasons: data.rejection_reasons,
      });

      setDiscovered(candidates);
      setDiscoveryDiagnostics({
        rejected_guesses: data.rejected_guesses,
        rejection_reasons: data.rejection_reasons || {},
      });
      setDiscoveryRan(true);
      setShowContentMatches(false);
      setSelectedIds(new Set(importableCandidates.map((candidate) => `discovered:${candidate.id}`)));

      if (importableCandidates.length > 0) {
        toast.success(`Found ${importableCandidates.length} company contact candidate${importableCandidates.length === 1 ? '' : 's'}.`);
      } else if (candidates.length > 0) {
        toast.info('Search completed with only low-confidence content matches. Enable the content filter to review them.');
      } else {
        toast.info('Search completed with no new personal contacts found.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error(formatApiError(err) || 'Personal contact discovery is not available on your plan.');
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setDiscovering(false);
    }
  };

  const buildContactPayload = (candidate) => {
    const email = (candidate.email || '').trim();
    const generic = isGenericCompanyEmail(email);
    const isGuess = candidate.candidate_type === 'generated_guess';
    const nameParts = (candidate.name || '').trim().split(/\s+/).filter(Boolean);
    return {
      company_id: companyId,
      full_name: candidate.name || '',
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      job_title: candidate.title || '',
      email,
      contact_source: generic ? 'auto_generated' : 'personal_discovery',
      email_type: generic ? 'generic_business' : (isGuess ? 'generated_guess' : 'named_business'),
      confidence_score: candidate.confidence || 50,
    };
  };

  const importCandidatesList = async (pending) => {
    let imported = 0;
    let skipped = 0;
    let failed = 0;
    let failedMessage = '';
    for (const candidate of pending) {
      const emailKey = contactEmailKey(candidate.email);
      try {
        await api.post('/contacts', buildContactPayload(candidate));
        setImportedEmails((prev) => new Set(prev).add(emailKey));
        imported += 1;
      } catch (err) {
        if (err?.response?.status === 400) skipped += 1;
        else {
          failed += 1;
          if (!failedMessage) failedMessage = formatApiError(err);
        }
      }
    }
    return { imported, skipped, failed, failedMessage };
  };

  useImperativeHandle(ref, () => ({
    discoverContacts,
    isDiscovering: discovering,
  }));

  const importCandidate = async (candidate) => {
    const email = (candidate.email || '').trim();
    if (!email) {
      toast.error('This candidate has no email to import.');
      return;
    }

    const emailKey = contactEmailKey(email);
    if (existingEmails.has(emailKey) || importedEmails.has(emailKey)) {
      toast.info(`${email} is already in Contacts.`);
      return;
    }

    setImportingIds((prev) => new Set(prev).add(candidate.id));
    try {
      await api.post('/contacts', buildContactPayload(candidate));
      setImportedEmails((prev) => new Set(prev).add(emailKey));
      toast.success(`Imported ${email}`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportingIds((prev) => {
        const next = new Set(prev);
        next.delete(candidate.id);
        return next;
      });
    }
  };

  const importSelected = async () => {
    const pending = discovered.filter((candidate) => {
      const rowId = `discovered:${candidate.id}`;
      const emailKey = contactEmailKey(candidate.email);
      return (
        candidate.candidate_type !== 'company_content'
        && selectedIds.has(rowId)
        && emailKey
        && !existingEmails.has(emailKey)
        && !importedEmails.has(emailKey)
      );
    });

    if (pending.length === 0) {
      toast.info('No selected contacts to import.');
      return;
    }

    setImportingAll(true);
    const { imported, skipped, failed, failedMessage } = await importCandidatesList(pending);
    setImportingAll(false);
    if (imported > 0) {
      toast.success(`Imported ${imported} contact${imported === 1 ? '' : 's'}.`);
      onImported?.();
    }
    if (skipped > 0) {
      toast.info(`Skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}.`);
    }
    if (failed > 0) {
      toast.error(failedMessage || `Failed to import ${failed} contact${failed === 1 ? '' : 's'}.`);
    }
  };

  const isImported = (candidate) => {
    const emailKey = contactEmailKey(candidate.email);
    return Boolean(emailKey && (existingEmails.has(emailKey) || importedEmails.has(emailKey)));
  };

  const discoveredRows = visibleDiscovered.map((candidate) => {
    const rowId = `discovered:${candidate.id}`;
    const imported = isImported(candidate);
    const importing = importingIds.has(candidate.id);
    const selected = selectedIds.has(rowId);
    const isContent = candidate.candidate_type === 'company_content';

    return (
      <tr key={rowId} className={`border-b border-gray-100 hover:bg-gray-50 ${selected ? 'bg-violet-50/40' : ''}`}>
        <td className="py-3 px-4">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleSelected(rowId)}
            disabled={imported || !candidate.email || isContent}
            aria-label={`Select ${candidate.email || candidate.name || 'contact'}`}
            data-testid={`select-discovered-${candidate.id}`}
          />
        </td>
        <td className="py-3 px-4 text-sm text-gray-900">{candidate.name || '-'}</td>
        <td className="py-3 px-4 text-sm text-gray-600">
          <span>{candidate.title || '-'}</span>
          {isHrCandidate(candidate) && (
            <span className="ml-2 inline-flex rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-violet-700">
              HR / People
            </span>
          )}
        </td>
        <td className="py-3 px-4 text-sm font-mono text-gray-700">{candidate.email || '-'}</td>
        <td className="py-3 px-4 text-sm text-gray-600">
          {SOURCE_LABELS[candidate.source_type] || candidate.source_type || '-'}
        </td>
        <td className="py-3 px-4">
          <ConfidenceBadge score={candidate.confidence ?? 0} />
        </td>
        <td className="py-3 px-4">
          <ContactTypeBadge candidateEmail={candidate.email} candidateType={candidate.candidate_type} />
        </td>
        <td className="py-3 px-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => importCandidate(candidate)}
            disabled={imported || importing || importingAll || !candidate.email || isContent}
            data-testid={`import-discovered-${candidate.id}`}
          >
            {importing ? (
              <>
                <SpinnerGap size={14} className="mr-1 animate-spin" /> Importing...
              </>
            ) : imported ? (
              'Imported'
            ) : (
              <>
                <UserPlus size={14} className="mr-1" /> Import to Contacts
              </>
            )}
          </Button>
        </td>
      </tr>
    );
  });

  const genericRows = genericCompanyContacts.map((contact, index) => {
    const rowId = `generic:${contact.id || contact.email || index}`;
    const selected = selectedIds.has(rowId);

    return (
      <tr key={rowId} className={`border-b border-gray-100 hover:bg-gray-50 ${selected ? 'bg-slate-50' : ''}`}>
        <td className="py-3 px-4">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleSelected(rowId)}
            aria-label={`Select ${contact.email || 'generic contact'}`}
            data-testid={`select-generic-${index}`}
          />
        </td>
        <td className="py-3 px-4 text-sm text-gray-900">{contact.full_name || '-'}</td>
        <td className="py-3 px-4 text-sm text-gray-600">{contact.job_title || '-'}</td>
        <td className="py-3 px-4 text-sm font-mono text-gray-700">{contact.email}</td>
        <td className="py-3 px-4 text-sm text-gray-600">Company import</td>
        <td className="py-3 px-4">
          <ConfidenceBadge score={contact.confidence_score ?? 40} />
        </td>
        <td className="py-3 px-4">
          <ContactTypeBadge contact={contact} />
        </td>
        <td className="py-3 px-4 text-xs text-gray-500">In Contacts</td>
      </tr>
    );
  });

  const selectedImportableCount = visibleDiscovered.filter((candidate) => {
    const rowId = `discovered:${candidate.id}`;
    const emailKey = contactEmailKey(candidate.email);
    return (
      candidate.candidate_type !== 'company_content'
      && selectedIds.has(rowId)
      && emailKey
      && !existingEmails.has(emailKey)
      && !importedEmails.has(emailKey)
    );
  }).length;

  return (
    <div className="mb-6 min-w-0 max-w-full" data-testid="discovered-contacts-panel">
      {isSuperAdmin && (
        <p
          className="mb-2 text-xs font-mono text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 inline-block"
          data-testid="pcd-ui-loaded-debug"
        >
          Personal Contact Discovery UI loaded
        </p>
      )}
      <div className="flex flex-col gap-3 mb-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Discovered Contacts</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Find named people and personal email patterns from website, web search, and LinkedIn snippets.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2">
          {discovered.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={importSelected}
              disabled={discovering || importingAll || selectedImportableCount === 0}
              className="w-full sm:w-auto"
              data-testid="import-all-discovered-btn"
            >
              {importingAll ? (
                <>
                  <SpinnerGap size={14} className="mr-1 animate-spin" /> Importing...
                </>
              ) : (
                <>
                  <UsersThree size={14} className="mr-1" /> Import Selected ({selectedImportableCount})
                </>
              )}
            </Button>
          )}
          {showFindButton && (
            <Button
              size="sm"
              onClick={discoverContacts}
              disabled={discovering || !hasDomain}
              className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto"
              data-testid="find-company-contacts-btn"
            >
              {discovering ? (
                <>
                  <SpinnerGap size={14} className="mr-1 animate-spin" /> Finding company contacts...
                </>
              ) : (
                <>
                  <MagnifyingGlass size={14} className="mr-1" /> Find Company Contacts
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {!hasDomain && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
          Add a company domain to enable personal contact discovery.
        </p>
      )}

      {genericCompanyContacts.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">
            Generic Company Emails
          </h4>
          <ContactsTable
            rows={genericRows}
            emptyMessage="No generic company emails on this company yet."
          />
        </div>
      )}

      {discoveryRan && visibleDiscovered.length === 0 && (
        <ContactsTable
          rows={[]}
          emptyMessage="No personal contacts were found. Verify that SERP_API_KEY is configured and try again."
        />
      )}

      {discovered.length > 0 && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
            <h4 className="text-xs font-mono uppercase tracking-wider text-gray-500">
              Personal Contact Candidates
            </h4>
            {contentMatchCount > 0 && (
              <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={showContentMatches}
                  onChange={(event) => setShowContentMatches(event.target.checked)}
                  data-testid="show-content-matches-toggle"
                />
                Show low-confidence content matches ({contentMatchCount})
              </label>
            )}
          </div>
          {discoveryDiagnostics?.rejected_guesses > 0 && (
            <p className="text-xs text-gray-500 mb-2" data-testid="discovery-rejection-diagnostics">
              Rejected {discoveryDiagnostics.rejected_guesses} non-person matches
              {Object.keys(discoveryDiagnostics.rejection_reasons || {}).length > 0
                ? `: ${Object.entries(discoveryDiagnostics.rejection_reasons).map(([reason, count]) => `${reason} (${count})`).join(', ')}`
                : '.'}
            </p>
          )}
          <ContactsTable
            rows={discoveredRows}
            emptyMessage={showContentMatches
              ? 'No discovery candidates found.'
              : 'No personal contact candidates found. Enable content matches to review filtered page/content entities.'}
          />
        </div>
      )}
    </div>
  );
});

export default DiscoveredContactsPanel;
